import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Modal } from "@/components/Shared";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";

import GrantExistingPanel from "./grant/GrantExistingPanel";
import GrantCreateForm from "./grant/GrantCreateForm";
import GrantResultPanel from "./grant/GrantResultPanel";

/**
 * Shared "Generate portal access" modal — orchestrator. Owns auth state +
 * async handlers; delegates rendering to focused sub-components under
 * /components/grant/ (Phase 2.5 split from a 364-line monolith).
 *
 * Props:
 *   open, onClose
 *   role        — "partner" | "merchant"
 *   subject     — { partner_id, partner_name }  OR  { merchant_name, merchant_id? }
 *
 * Calls POST /api/admin/access/grant. Backend creates a users row + emails the
 * temp password via Resend; we also display the temp password here in case
 * the email isn't configured.
 */
export default function GrantAccessModal({ open, onClose, role, subject }) {
  // Pre-fill email + name from the partner/merchant row. Both stay editable.
  const initialEmail = subject?.email || "";
  const initialName  = subject?.partner_name || subject?.merchant_name || "";
  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  // For merchant grants: lazy-loaded preview of all related MIDs (across
  // PGs/LOBs) the portal user will see post-grant.
  const [relatedMids, setRelatedMids] = useState(null);
  const [relatedLoading, setRelatedLoading] = useState(false);
  // Existing-access detection: when the subject already has a portal user,
  // we flip the modal into "manage" mode (revoke + re-issue) instead of
  // "create new". Loaded fresh on every open.
  const [existing, setExisting] = useState(null);  // null = unknown, false = none, {user} = found
  const [checking, setChecking] = useState(false);

  // Reset internal state whenever the subject changes (modal opens for a different row).
  const subjectKey = `${role}-${subject?.partner_id || subject?.merchant_name || ""}`;

  useEffect(() => {
    setEmail(initialEmail);
    setName(initialName);
    setResult(null);
    setCopied(false);
    setRelatedMids(null);
    setExisting(null);
    if (!open) return;
    // Check if access already exists. Backend returns {granted: true|false, user?}.
    setChecking(true);
    const params = role === "partner"
      ? { role, partner_id: subject?.partner_id || "" }
      : { role, merchant_name: subject?.merchant_name || "" };
    api
      .get("/admin/access/check", { params })
      .then(({ data }) => setExisting(data?.granted ? data.user : false))
      .catch(() => setExisting(false))
      .finally(() => setChecking(false));
    // Fetch related MIDs only for merchant grants where we know the name.
    if (role === "merchant" && subject?.merchant_name) {
      setRelatedLoading(true);
      api
        .get(`/admin/access/merchant-mids/${encodeURIComponent(subject.merchant_name)}`)
        .then(({ data }) => setRelatedMids(data))
        .catch(() => setRelatedMids({ error: true }))
        .finally(() => setRelatedLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectKey, open]);

  const submit = async () => {
    if (!email.trim()) { toast.error("Email is required"); return; }
    setBusy(true);
    try {
      const body = { role, email: email.trim().toLowerCase(), name: name.trim() || undefined };
      if (role === "partner") body.partner_id = subject?.partner_id;
      if (role === "merchant") body.merchant_name = subject?.merchant_name;
      const { data } = await api.post("/admin/access/grant", body);
      setResult(data);
      toast.success("Portal access granted — temp password emailed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't grant access");
    }
    setBusy(false);
  };

  // ---- Manage-existing actions: Revoke + Re-issue (option 5c) -----------
  // Both leave the linked partner/merchant intact. Revoke deletes the user
  // row entirely so admin can grant fresh access from scratch (potentially
  // with a different email). Re-issue cycles the password without losing the
  // user — useful when the user simply forgot their password.
  const revokeExisting = async () => {
    if (!existing?.id) return;
    const ok = await openConfirm({
      title: "Revoke portal access?",
      message: `${existing.name || existing.email} will lose access to the ${role} portal immediately. The linked ${role} record stays intact — you can grant fresh access afterwards. Continue?`,
      confirmLabel: "Revoke",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/admin/access/${existing.id}`);
      toast.success("Access revoked");
      // Drop straight into the create form — admin probably wants to re-issue
      // to a different email or clean up.
      setExisting(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
    setBusy(false);
  };

  const reissueExisting = async () => {
    if (!existing?.id) return;
    const ok = await openConfirm({
      title: "Re-issue credentials?",
      message: `A fresh temporary password will be generated and emailed to ${existing.email}. The user keeps their account and history but must set a new password on next sign-in.`,
      confirmLabel: "Send new password",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/access/${existing.id}/reset-password`);
      // Reuse the same "result" panel — same UX as a fresh grant.
      setResult({ ok: true, temp_password: data.temp_password, user: { email: data.email, name: existing.name } });
      toast.success("New password issued — emailed to the user");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-issue failed");
    }
    setBusy(false);
  };

  const close = () => {
    setEmail(initialEmail); setName(initialName);
    setResult(null); setCopied(false);
    setExisting(null);
    onClose?.();
  };

  const copyPw = async () => {
    if (!result?.temp_password) return;
    try {
      await navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) { /* ignore */ }
  };

  const title = result
    ? (existing ? "Credentials re-issued" : "Portal access granted")
    : (existing ? `Manage ${role} portal access` : `Generate ${role} portal access`);

  return (
    <Modal open={open} onClose={close} title={title} testid="grant-access-modal" size="md">
      {/* While we check whether access already exists, show a brief
          placeholder instead of flashing the create form. */}
      {!result && existing === null && checking && (
        <div className="py-8 text-center text-[12.5px] text-[var(--muted)]" data-testid="grant-checking">
          Checking existing access…
        </div>
      )}

      {!result && existing && (
        <GrantExistingPanel
          role={role}
          existing={existing}
          busy={busy}
          onClose={close}
          onRevoke={revokeExisting}
          onReissue={reissueExisting}
        />
      )}

      {!result && existing === false && (
        <GrantCreateForm
          role={role}
          subject={subject}
          email={email} setEmail={setEmail}
          name={name} setName={setName}
          busy={busy}
          relatedMids={relatedMids}
          relatedLoading={relatedLoading}
          onCancel={close}
          onSubmit={submit}
          formKey={subjectKey}
        />
      )}

      {result && (
        <GrantResultPanel
          role={role}
          result={result}
          copied={copied}
          onCopy={copyPw}
          onClose={close}
        />
      )}
    </Modal>
  );
}
